/**
 * Wind System — per-turn-cycle horizontal force on projectiles.
 * Wind re-rolls every SHIFT_INTERVAL turn cycles.
 */

import { WIND } from '../constants';
import { random } from '../terrain/rng';
import { playSound } from '../audio';
import type { GameState } from '../types';

/** Roll a new random wind value. 15% chance of calm (0), otherwise -0.15..+0.15. */
export function rollNewWind(): number {
  if (random() < WIND.CALM_CHANCE) return 0;
  const magnitude = random() * WIND.MAX_MAGNITUDE;
  const direction = random() < 0.5 ? -1 : 1;
  return magnitude * direction;
}

/**
 * Check if wind should shift this turn cycle and apply it.
 * Called when a new turn cycle begins (after advanceTurn detects isNewTurn).
 */
export function maybeShiftWind(state: GameState): void {
  if (state.turnNumber % WIND.SHIFT_INTERVAL !== 0) return;

  const previousWind = state.wind;
  state.wind = rollNewWind();

  if (previousWind !== state.wind) {
    state.windShiftPending = true;
    state.windShiftTimer = WIND.SHIFT_DISPLAY_TIME;
    playSound('wind_change');
  }
}

/** Decay the wind shift notification timer. Called each visual frame. */
export function updateWindShift(state: GameState, dt: number): void {
  if (!state.windShiftPending) return;
  state.windShiftTimer -= dt;
  if (state.windShiftTimer <= 0) {
    state.windShiftPending = false;
    state.windShiftTimer = 0;
  }
}

/**
 * Format wind for HUD display.
 * Returns { text, arrows } — e.g. { text: "0.06", arrows: ">>" }
 */
export function formatWind(wind: number): { display: string; isCalm: boolean } {
  if (Math.abs(wind) < 0.005) return { display: '~ CALM ~', isCalm: true };

  const arrowCount = Math.abs(wind) > 0.10 ? 3 : Math.abs(wind) > 0.05 ? 2 : 1;
  const arrowChar = wind > 0 ? '>' : '<';
  const arrows = arrowChar.repeat(arrowCount);
  return { display: `${arrows} ${Math.abs(wind).toFixed(2)}`, isCalm: false };
}
